import {SelectItem} from "@mantine/core";
import {Entity} from "@ots-frontend/ots-data";
import {useCallback, useMemo, useState} from "react";
import {BaseMultiSelect, BaseMultiSelectProps} from "../base-multi-select";
import {useTranslation} from "react-i18next";

export type BaseEntityMultiSelectProps<E extends Entity> = Omit<BaseMultiSelectProps, 'data'> & {
  entities: E[];
  valueMapper?: (e: E) => string;
  nameMapper?: (e: E) => string;
  onEntityChange?: (entities: E[]) => void;
  searchResultsOnly?: boolean;
};

export function BaseEntityMultiSelect<E extends Entity>(
  props: BaseEntityMultiSelectProps<E>
) {

  const {t} = useTranslation(['common'])

  const {
    entities,
    valueMapper = (e) => e.id,
    nameMapper = (e) => e.displayName,
    onChange,
    onEntityChange,
    onSearchChange,
    searchValue,
    searchable = true,
    searchResultsOnly = false,
    nothingFound = t('common.state.noresults', 'No Results'),
    ...rest
  } = props;

  const [internalSearchValue, setInternalSearchValue] = useState('');

  const _onSearchChange = useCallback((query: string) => {
    setInternalSearchValue(query)
  }, [])

  const _onChange = useCallback(
    (value: string[]) => {
      if (onEntityChange) {
        let selectedEntities: E[] = [];

        if (value) {
          selectedEntities = entities.filter((e) => typeof value.find(v => v === e.id) !== 'undefined');
        }

        onEntityChange(selectedEntities);
      }

      if (onChange) {
        onChange(value);
      }
    },
    [entities, onChange, onEntityChange]
  );

  const data = useMemo(() => {
    const items: SelectItem[] = []

    for (const e of entities) {
      const item = {
        value: valueMapper(e),
        label: nameMapper(e),
      }

      if (searchResultsOnly) {
        if (internalSearchValue.length > 0) {
          if (item.value.toLowerCase().includes(internalSearchValue.toLowerCase())) {
            items.push(item)
          } else if (item.label.toLowerCase().includes(internalSearchValue.toLowerCase())) {
            items.push(item)
          }
        }
      } else {
        items.push(item)
      }
    }

    return items
  }, [entities, nameMapper, valueMapper, internalSearchValue, searchResultsOnly])

  return (
    <BaseMultiSelect
      data={data}
      onChange={_onChange}
      searchable={searchable}
      onSearchChange={searchResultsOnly ? _onSearchChange : onSearchChange}
      searchValue={searchResultsOnly ? internalSearchValue : searchValue}
      nothingFound={nothingFound}
      {...rest} />
  );
}
